import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Flex,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import CommonCheckTable from 'common/components/checktable';
import { getApi, postApi, deleteApi, deleteManyApi } from 'common/services/api';
import { API_PATHS } from 'common/services/apiPaths';
import { HasAccess } from 'common/utils/accessUtils';
import { accessModuleTitles } from 'common/constant';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import DateFormatter from 'common/utils/dateFormater';
import { globalStyles } from 'common/theme/styles';
import viewIcon from 'common/assets/img/viewIcon.png';
import deleteIcon from 'common/assets/img/deleteIcon.png';
import { EmailIcon } from '@chakra-ui/icons';
import emailTemplateService from 'services/Recruit/services/emailTemplate';
import TextEditor from 'common/components/textEditor/TextEditor';
import { validateEmail, formatJobTitle } from 'common/utils/validationUtils';
import CommonDeleteModel from 'common/components/commonDeleteModel';
import { getStatusBadgeStyle } from "common/utils/statusColor";
import {
  getTableFilters,
  saveTableFilters,
  clearTableFilters,
  getInitialFilterState
} from "common/utils/filterUtils";

const BackgroundVerification = () => {
  const title = "Background Verification";
  const TABLE_NAME = 'backgroundVerification'; // Define table name constant
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [displaySearchData, setDisplaySearchData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchbox, setSearchbox] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.searchbox || "";
  });
  const [metadata, setMetadata] = useState({});
  const [permission] = HasAccess([accessModuleTitles.bgVerification]);
  const navigate = useNavigate();
  const colors = globalStyles.colors;

  // Create modal state
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const [applications, setApplications] = useState([]);
  const [isApplicationsLoading, setIsApplicationsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);

  // View modal state
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const [viewData, setViewData] = useState(null);
  const [isLoadingView, setIsLoadingView] = useState(false);

  // Delete confirmation dialog state
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [deleteId, setDeleteId] = useState(null);
  const cancelRef = useRef();

  // Bulk delete state
  const [deleteMany, setDeleteMany] = useState(false);

  // Loading states for individual actions
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [createForm, setCreateForm] = useState({
    applicationId: '',
    previousCompanyEmail: '',
    previousCompanyName: '',
    previousCompanyContactPerson: '',
    previousCompanyContactNumber: '',
    emailTemplateId: '',
    emailSubject: 'Background Verification Request',
    emailContent: '',
    employeeFullName: '',
    employeeId: '',
    jobTitle: '',
    department: '',
  });

  const [contactNumberError, setContactNumberError] = useState('');

  // Status filter options
  // Note: CommonCheckTable automatically adds "All Status" option, so we don't need to include it here
  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "sent", label: "Sent" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
    { value: "expired", label: "Expired" },
    { value: "rejected", label: "Rejected" },
  ];

  const [currentStatusFilter, setCurrentStatusFilter] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.currentStatusFilter || "";
  });

  // Save filters whenever they change
  useEffect(() => {
    const filters = {
      currentStatusFilter,
      searchbox: searchbox || "",
    };
    saveTableFilters(TABLE_NAME, filters);
  }, [currentStatusFilter, searchbox]);

  const handleStatusFilterChange = useCallback((selectedStatus) => {
    setCurrentStatusFilter(selectedStatus);
    setCurrentPage(1);
  }, []);


  // Fetch eligible applications for create modal
  useEffect(() => {
    const fetchApplications = async () => {
      setIsApplicationsLoading(true);
      try {
        const result = await getApi(API_PATHS.BACKGROUND_VERIFICATION_ELIGIBLE_APPLICATIONS);
        setApplications(result?.data?.data || []);
      } catch (e) {
        console.error("Failed to fetch applications:", e);
        toast.error("Failed to load eligible applications");
      } finally {
        setIsApplicationsLoading(false);
      }
    };

    const fetchEmailTemplates = async () => {
      setIsTemplatesLoading(true);
      try {
        const response = await emailTemplateService.getTemplates({ allData: true, isActive: true });
        const templates = response?.data?.data ?? response?.data ?? [];
        setEmailTemplates(Array.isArray(templates) ? templates : []);
      } catch (e) {
        console.error("Failed to fetch email templates:", e);
        setEmailTemplates([]);
      } finally {
        setIsTemplatesLoading(false);
      }
    };

    if (isCreateOpen) {
      fetchApplications();
      fetchEmailTemplates();
    }
  }, [isCreateOpen]);

  // Fetch background verifications
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      let endpoint = `${API_PATHS.BACKGROUND_VERIFICATION}?page=${currentPage}&limit=${pageSize}`;

      if (currentStatusFilter) {
        endpoint += `&status=${currentStatusFilter}`;
      }

      if (searchbox) {
        endpoint += `&search=${encodeURIComponent(searchbox)}`;
      }

      const result = await getApi(endpoint);

      // Format data for CommonCheckTable with FLAT properties (not nested objects)
      const baseIndex = (result?.data?.metadata?.page - 1) * result?.data?.metadata?.limit;
      const formattedData = (result?.data?.data || []).map((item, index) => ({
        _id: item._id,
        srNo: baseIndex + index + 1,
        candidateName: item.candidate?.name || '-',
        previousCompanyName: item.previousCompanyName || '-',
        previousCompanyEmail: item.previousCompanyEmail || '-',
        status: item.status || 'pending',
        sentAt: item.sentAt,
        expiryDate: item.expiryDate,
        createdAt: item.createdAt,
        rawData: item
      }));

      setData(formattedData);
      setMetadata(result?.data?.metadata || {});
    } catch (e) {
      console.error("Failed to fetch background verifications:", e);
      toast.error("Failed to load background verifications");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, currentStatusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = useCallback((term) => {
    const trimmed = term?.trim() ?? "";
    setSearchbox(trimmed);
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      // Manual trigger if already on page 1
      fetchData();
    }
  }, [currentPage, fetchData]);

  // Handle create
  const handleCreate = async () => {
    try {
      // Validation
      if (!createForm.applicationId || !createForm.previousCompanyEmail || !createForm.previousCompanyName || !createForm.emailTemplateId) {
        toast.warning("Please fill in all required fields (Application, Company Name, Contact Email, and Email Template are mandatory)");
        return;
      }

      // Validate email format
      if (!validateEmail(createForm.previousCompanyEmail)) {
        toast.error("Please enter a valid email address");
        return;
      }

      // Validate field lengths
      if (createForm.previousCompanyName.length > 256) {
        toast.warning("Company name must not exceed 256 characters");
        return;
      }

      if (createForm.previousCompanyContactPerson && createForm.previousCompanyContactPerson.length > 256) {
        toast.warning("Contact person name must not exceed 256 characters");
        return;
      }

      if (createForm.previousCompanyContactNumber) {
        if (createForm.previousCompanyContactNumber.length !== 10) {
          toast({
            title: "Validation Error",
            description: "Contact number must be exactly 10 digits",
            status: "warning",
            duration: 3000,
            position: "top-right",
            isClosable: true,
          });
          return;
        }
        if (!/^[6-9]/.test(createForm.previousCompanyContactNumber)) {
          toast({
            title: "Validation Error",
            description: "Please enter a valid Indian mobile number starting with 6, 7, 8, or 9",
            status: "warning",
            duration: 3000,
            position: "top-right",
            isClosable: true,
          });
          return;
        }
      }

      setIsCreating(true);

      // Create background verification
      const response = await postApi(API_PATHS.BACKGROUND_VERIFICATION, createForm);

      if (response?.data?.data) {
        toast.success("Background verification created successfully");

        // Reset form
        setCreateForm({
          applicationId: '',
          previousCompanyEmail: '',
          previousCompanyName: '',
          previousCompanyContactPerson: '',
          previousCompanyContactNumber: '',
          emailTemplateId: '',
          emailSubject: 'Background Verification Request',
          emailContent: '',
          employeeFullName: '',
          employeeId: '',
          jobTitle: '',
          department: '',
        });

        onCreateClose();
        fetchData();
      }
    } catch (error) {
      console.error('Error creating background verification:', error);
      toast.error(error?.response?.data?.message || "Failed to create background verification");
    } finally {
      setIsCreating(false);
    }
  };

  // Handle send email
  const handleSendEmail = async (verificationId) => {
    try {
      setSendingEmailId(verificationId);
      await postApi(`${API_PATHS.BACKGROUND_VERIFICATION}/${verificationId}/send-email`);
      toast.success("Verification email sent successfully");
      fetchData();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error("Failed to send verification email");
    } finally {
      setSendingEmailId(null);
    }
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (id) => {
    console.log('Delete clicked with ID:', id, 'Type:', typeof id);
    if (!id || id === 'undefined' || String(id).includes('undefined')) {
      console.error('Invalid ID passed to handleDeleteClick:', id);
      toast.error("Invalid verification ID. Please refresh and try again.");
      return;
    }
    setDeleteId(id);
    onDeleteOpen();
  };

  // Confirm and execute delete
  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      setDeletingId(deleteId);
      await deleteApi(`${API_PATHS.BACKGROUND_VERIFICATION}/${deleteId}`, '');
      toast.success("Background verification deleted successfully");
      fetchData();
      onDeleteClose();
    } catch (error) {
      console.error('Error deleting background verification:', error);
      toast.error("Failed to delete background verification");
    } finally {
      setDeletingId(null);
      setDeleteId(null);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async (ids) => {
    try {
      setDeleteMany(false);
      const response = await deleteManyApi(`${API_PATHS.BACKGROUND_VERIFICATION}/deleteMany`, ids);
      if (response.status === 200) {
        setSelectedValues([]);
        toast.success(
          `Background verification${selectedValues.length > 1 ? 's' : ''} deleted successfully`
        );
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting background verifications:', error);
      toast.error("Failed to delete background verifications");
    }
  };

  // Handle view details
  const handleViewDetails = async (verificationId) => {
    try {
      setIsLoadingView(true);
      const response = await getApi(`${API_PATHS.BACKGROUND_VERIFICATION}/${verificationId}`);
      if (response?.data?.data) {
        setViewData(response.data.data);
        onViewOpen();
      }
    } catch (error) {
      console.error('Error fetching verification details:', error);
      toast.error("Failed to load verification details");
    } finally {
      setIsLoadingView(false);
    }
  };

  // Handle application selection
  const handleApplicationChange = (e) => {
    const appId = e.target.value;

    // Auto-fill candidate details if available
    const selectedApp = applications.find(app => app._id === appId);
    if (selectedApp) {

      const jobTitle = selectedApp.candidateDetails?.currentJobTitle || selectedApp.job?.postingTitle || '';

      // Extract department name properly
      let departmentName = '';
      if (selectedApp.job?.department) {
        if (typeof selectedApp.job.department === 'object' && selectedApp.job.department.name) {
          departmentName = selectedApp.job.department.name;
        } else if (typeof selectedApp.job.department === 'string') {
          departmentName = selectedApp.job.department;
        }
      }

      setCreateForm(prev => ({
        ...prev,
        applicationId: appId,
        employeeFullName: selectedApp.candidateDetails?.name || '',
        jobTitle: formatJobTitle(jobTitle),
        department: departmentName,
        previousCompanyName: selectedApp.candidateDetails?.currentCompany || selectedApp.candidateDetails?.previousCompany || prev.previousCompanyName,
      }));
    } else {
      setCreateForm({ ...createForm, applicationId: appId });
    }
  };

  // Handle email template selection
  const handleTemplateChange = (e) => {
    const templateId = e.target.value;

    if (!templateId) {
      setCreateForm({
        ...createForm,
        emailTemplateId: '',
        emailSubject: 'Background Verification Request',
        emailContent: '',
      });
      return;
    }

    const selectedTemplate = emailTemplates.find(t => t._id === templateId);

    if (selectedTemplate) {
      // Replace placeholders - use 'content' field from email template schema
      let emailContent = selectedTemplate.content || selectedTemplate.body || '';

      // Replace [Contact Person] with the contact person's name
      if (createForm.previousCompanyContactPerson) {
        emailContent = emailContent.replace(/\[Contact Person\]/g, createForm.previousCompanyContactPerson);
        emailContent = emailContent.replace(/\[ContactPerson\]/g, createForm.previousCompanyContactPerson);
        emailContent = emailContent.replace(/\[Recipient Name\]/g, createForm.previousCompanyContactPerson);
      }

      // Replace [Candidate Name] with employee name
      if (createForm.employeeFullName) {
        emailContent = emailContent.replace(/\[Candidate Name\]/g, createForm.employeeFullName);
        emailContent = emailContent.replace(/\[Employee Name\]/g, createForm.employeeFullName);
      }


      // Note: [Verification Link] placeholder should be kept as-is, it will be replaced when email is sent

      setCreateForm(prev => ({
        ...prev,
        emailTemplateId: templateId,
        emailSubject: selectedTemplate.subject || prev.emailSubject,
        emailContent: emailContent,
      }));
    }
  };

  // Table columns
  const tableColumns = useMemo(() => [
    {
      Header: "#",
      accessor: "_id",
      isSortable: false,
      width: 5,
    },
    {
      Header: "Candidate Name",
      accessor: "candidateName",
      cell: (cell) => (
        <Text>{cell?.value || '-'}</Text>
      ),
    },
    {
      Header: "Previous Company",
      accessor: "previousCompanyName",
      cell: (cell) => <Text>{cell?.value || '-'}</Text>,
    },
    {
      Header: "Contact Email",
      accessor: "previousCompanyEmail",
      cell: (cell) => <Text>{cell?.value || '-'}</Text>,
    },
    {
      Header: "Status",
      accessor: "status",
      cell: (cell) => {
        const status = cell?.value || 'pending';
        const statusStyle = getStatusBadgeStyle(status);

        return (
          <Flex align="center" gap={2}>
            <Box bg={statusStyle.color} h="8px" w="8px" borderRadius="full" />
            <Text as="span" color={statusStyle.color} style={{ color: statusStyle.color }} fontWeight="500">
              {status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
            </Text>
          </Flex>
        );
      },
    },
    {
      Header: "Sent Date",
      accessor: "sentAt",
      cell: (cell) => (
        <Text>{cell?.value ? DateFormatter(cell?.value) : '-'}</Text>
      ),
    },
    {
      Header: "Expiry Date",
      accessor: "expiryDate",
      cell: (cell) => (
        <Text>{cell?.value ? DateFormatter(cell?.value) : '-'}</Text>
      ),
    },
    {
      Header: "Created Date",
      accessor: "createdAt",
      cell: (cell) => (
        <Text>{DateFormatter(cell?.value)}</Text>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      isSortable: false,
      center: true,
      cell: ({ row }) => {
        const verification = row.original.rawData || row.original;
        const status = verification?.status;
        const verificationId = row.original._id || verification?._id;
        const isSending = sendingEmailId === verificationId;
        const isDeleting = deletingId === verificationId;

        return (
          <Flex justify="center" align="center" w="100%">
            <Menu>
              <MenuButton
                as={IconButton}
                size="sm"
                variant="ghost"
                fontWeight="bold"
                fontSize="xl"
                color="gray.600"
                _hover={{ bg: "gray.100" }}
                isDisabled={isSending || isDeleting}
              >
                ⋮
              </MenuButton>
              <MenuList minW="150px" shadow="md">
                {status === 'pending' && (
                  <MenuItem
                    icon={<EmailIcon />}
                    onClick={() => handleSendEmail(verificationId)}
                    isDisabled={isSending}
                  >
                    {isSending ? <Spinner size="sm" mr={2} /> : null}
                    {isSending ? 'Sending...' : 'Send Email'}
                  </MenuItem>
                )}
                <MenuItem
                  icon={<img src={viewIcon} alt="view" style={{ width: '16px' }} />}
                  onClick={() => handleViewDetails(verificationId)}
                >
                  View Details
                </MenuItem>
                {permission?.delete && (
                  <MenuItem
                    color="red.500"
                    icon={<img src={deleteIcon} alt="delete" style={{ width: '16px' }} />}
                    onClick={() => handleDeleteClick(verificationId)}
                    isDisabled={isDeleting}
                  >
                    Delete
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          </Flex>
        );
      },
    },
  ], [permission, sendingEmailId, deletingId, handleSendEmail, handleViewDetails, handleDeleteClick]);

  const dataColumn = [...tableColumns];
  const defaultVisibleHeaders = [
    "Candidate Name",
    "Previous Company",
    "Status",
    "Sent Date",
    "Expiry Date",
    "Actions",
  ];
  const defaultVisibleColumns = tableColumns.filter((column) =>
    defaultVisibleHeaders.includes(column.Header)
  );

  return (
    <div>
      <CommonCheckTable
        title={title}
        showInlineTitle={true}
        isLoading={isLoading}
        columnData={defaultVisibleColumns}
        dataColumn={defaultVisibleColumns}
        allData={data}
        tableData={data}
        checkBox={true}
        setSearchDisplay={setDisplaySearchData}
        tableCustomFields={[]}
        access={permission}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        setDelete={setDeleteMany}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        searchbox={searchbox}
        setSearchbox={setSearchbox}
        metadata={metadata}
        fetchData={fetchData}
        isOpen={isCreateOpen}
        onOpen={onCreateOpen}
        onClose={onCreateClose}
        statusFilter={true}
        filterStatusOptions={statusOptions}
        currentStatusFilter={currentStatusFilter}
        handleSearchChange={handleSearchChange}
      />

      {/* Create Background Verification Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="600px" maxH="85vh">
          <ModalHeader>Create Background Verification</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" maxH="calc(85vh - 140px)">
            <VStack spacing={4} align="stretch">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  Background verification can only be initiated for applications that have completed LOI,
                  document verification, and onboarding checklist.
                </AlertDescription>
              </Alert>

              <FormControl isRequired>
                <FormLabel>Select Application</FormLabel>
                <Select
                  placeholder={isApplicationsLoading ? "Loading..." : "Select application"}
                  value={createForm.applicationId}
                  onChange={handleApplicationChange}
                  isDisabled={isApplicationsLoading}
                >
                  {applications.length === 0 && !isApplicationsLoading ? (
                    <option value="" disabled>No eligible applications found</option>
                  ) : (
                    applications
                      .filter((app) =>
                        app?.job?.deleted !== true &&
                        app?.candidateDetails?.deleted !== true
                      )
                      .map((app) => (
                        <option key={app._id} value={app._id}>
                          {app.candidateDetails?.name} - {app.job?.postingTitle}
                        </option>
                      ))
                  )}
                </Select>
                {isApplicationsLoading && <Spinner size="sm" mt={2} />}
                {applications.length === 0 && !isApplicationsLoading && (
                  <Text fontSize="sm" color="red.500" mt={2}>
                    No eligible applications. Applications must have completed LOI, document verification, and onboarding checklist.
                  </Text>
                )}
              </FormControl>

              <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                <Text fontWeight="bold" mb={3}>Previous Company Details</Text>

                <FormControl isRequired mb={3}>
                  <FormLabel>Company Name</FormLabel>
                  <Input
                    value={createForm.previousCompanyName}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 256) {
                        setCreateForm({ ...createForm, previousCompanyName: value });
                      }
                    }}
                    placeholder="Enter previous company name (auto-filled from application)"
                    maxLength={256}
                  />
                </FormControl>

                <FormControl isRequired mb={3}>
                  <FormLabel>Contact Email</FormLabel>
                  <Input
                    type="email"
                    value={createForm.previousCompanyEmail}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 256) {
                        setCreateForm({ ...createForm, previousCompanyEmail: value });
                      }
                    }}
                    placeholder="hr@previouscompany.com"
                    maxLength={256}
                  />
                </FormControl>

                <FormControl mb={3}>
                  <FormLabel>Contact Person</FormLabel>
                  <Input
                    value={createForm.previousCompanyContactPerson}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 256) {
                        setCreateForm({ ...createForm, previousCompanyContactPerson: value });
                      }
                    }}
                    placeholder="Enter contact person name"
                    maxLength={256}
                  />
                </FormControl>

                <FormControl isInvalid={contactNumberError}>
                  <FormLabel>Contact Number</FormLabel>
                  <Input
                    value={createForm.previousCompanyContactNumber}
                    type="tel"
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setCreateForm({ ...createForm, previousCompanyContactNumber: value });
                      // Clear error on change
                      if (contactNumberError) setContactNumberError('');
                    }}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        "Backspace",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                        "Delete"
                      ];
                      const isCopyPaste =
                        (e.ctrlKey || e.metaKey) &&
                        ["c", "v", "x", "a"].includes(e.key.toLowerCase());
                      const isNumber = /^[0-9]$/.test(e.key);
                      if (isCopyPaste) return;
                      const currentValue = e.currentTarget.value;
                      if (currentValue.length >= 10 && !allowedKeys.includes(e.key)) {
                        e.preventDefault();
                        return;
                      }
                      if (!isNumber && !allowedKeys.includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData('text');
                      const digitsOnly = pastedText.replace(/\D/g, '').slice(0, 10);
                      setCreateForm({ ...createForm, previousCompanyContactNumber: digitsOnly });
                      if (contactNumberError) setContactNumberError('');
                    }}
                    onBlur={() => {
                      const value = createForm.previousCompanyContactNumber;
                      if (value) {
                        if (value.length !== 10) {
                          setContactNumberError('Contact number must be exactly 10 digits');
                        } else if (!/^[6-9]/.test(value)) {
                          setContactNumberError('Contact number must start with 6, 7, 8, or 9');
                        }
                      }
                    }}
                    onInput={(e) => {
                      if (e.target.value.length > 10) {
                        e.target.value = e.target.value.slice(0, 10);
                      }
                    }}
                    placeholder="Enter 10-digit mobile number"
                    maxLength={10}
                  />
                  {contactNumberError && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {contactNumberError}
                    </Text>
                  )}
                </FormControl>
              </Box>

              <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                <Text fontWeight="bold" mb={3}>Email Details</Text>

                <FormControl isRequired mb={3}>
                  <FormLabel>Email Template</FormLabel>
                  <Select
                    placeholder="Select email template"
                    value={createForm.emailTemplateId}
                    onChange={handleTemplateChange}
                    isDisabled={isTemplatesLoading}
                  >
                    {emailTemplates.map((template) => (
                      <option key={template._id} value={template._id}>
                        {template.name} ({template.category || "custom"})
                      </option>
                    ))}
                  </Select>
                  {!isTemplatesLoading && emailTemplates.length === 0 && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                      No email templates available. Please create one first.
                    </Text>
                  )}
                </FormControl>

                <FormControl mb={3}>
                  <FormLabel>Email Subject</FormLabel>
                  <Input
                    value={createForm.emailSubject}
                    onChange={(e) => setCreateForm({ ...createForm, emailSubject: e.target.value })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email Content</FormLabel>
                  <Box minH="200px" sx={{
                    '.quill': { minHeight: '200px' },
                    '.ql-container': { minHeight: '150px' },
                    '.ql-editor': { minHeight: '150px' }
                  }}>
                    <TextEditor
                      toolbarId="bg-verification-email-content"
                      value={createForm.emailContent}
                      handleChange={(value) => {
                        const cleanedValue = value === "<p><br></p>" ? "" : value;
                        setCreateForm({ ...createForm, emailContent: cleanedValue });
                      }}
                    />
                  </Box>
                  {createForm.emailContent && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Content length: {createForm.emailContent.length} characters
                    </Text>
                  )}
                </FormControl>
              </Box>

              <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                <Text fontWeight="bold" mb={3}>Employee Details (Auto-filled from Application)</Text>

                <HStack spacing={3} mb={3}>
                  <FormControl>
                    <FormLabel>Full Name</FormLabel>
                    <Input
                      value={createForm.employeeFullName}
                      isReadOnly
                      bg="gray.100"
                      cursor="not-allowed"
                      placeholder="Auto-filled from application"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Employee ID (Optional)</FormLabel>
                    <Input
                      value={createForm.employeeId}
                      onChange={(e) => setCreateForm({ ...createForm, employeeId: e.target.value })}
                      placeholder="Enter employee ID if known"
                    />
                  </FormControl>
                </HStack>

                <HStack spacing={3}>
                  <FormControl>
                    <FormLabel>Job Title</FormLabel>
                    <Input
                      value={createForm.jobTitle}
                      isReadOnly
                      bg="gray.100"
                      cursor="not-allowed"
                      placeholder="Auto-filled from application"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Department</FormLabel>
                    <Input
                      value={createForm.department}
                      isReadOnly
                      bg="gray.100"
                      cursor="not-allowed"
                      placeholder="Auto-filled from application"
                    />
                  </FormControl>
                </HStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>
              Cancel
            </Button>
            <Button
              bg="#3C2FC0"
              color="white"
              _hover={{ bg: "#2F23A0" }}
              onClick={handleCreate}
              isLoading={isCreating}
              loadingText="Creating..."
              isDisabled={applications.length === 0 || !createForm.applicationId}
            >
              Create Background Verification
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Details Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader bg="#3C2FC0" color="white">
            Background Verification Details
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={6}>
            {isLoadingView ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" color="#3C2FC0" />
                <Text mt={4}>Loading details...</Text>
              </Box>
            ) : viewData ? (
              <VStack spacing={6} align="stretch">
                {/* Basic Information */}
                <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                  <Text fontWeight="bold" fontSize="lg" mb={3}>Basic Information</Text>
                  <VStack align="stretch" spacing={2}>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Candidate Name:</Text>
                      <Text>{viewData.candidate?.name || '-'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Job Position:</Text>
                      <Text>{viewData.job?.postingTitle || '-'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Status:</Text>
                      <HStack spacing={2}>
                        <Box
                          bg={getStatusBadgeStyle(viewData.status || 'pending').color}
                          h="8px"
                          w="8px"
                          borderRadius="full"
                        />
                        <Text as="span" color={getStatusBadgeStyle(viewData.status || 'pending').color} style={{ color: getStatusBadgeStyle(viewData.status || 'pending').color }} fontWeight="500">
                          {viewData.status?.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) || '-'}
                        </Text>
                      </HStack>
                    </HStack>
                  </VStack>
                </Box>

                {/* Previous Company Details */}
                <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                  <Text fontWeight="bold" fontSize="lg" mb={3}>Previous Company Details</Text>
                  <VStack align="stretch" spacing={2}>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Company Name:</Text>
                      <Text>{viewData.previousCompanyName || '-'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Contact Person:</Text>
                      <Text>{viewData.previousCompanyContactPerson || '-'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Contact Email:</Text>
                      <Text>{viewData.previousCompanyEmail || '-'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Contact Number:</Text>
                      <Text>{viewData.previousCompanyContactNumber || '-'}</Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* Employee Details */}
                <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                  <Text fontWeight="bold" fontSize="lg" mb={3}>Employee Details</Text>
                  <VStack align="stretch" spacing={2}>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Full Name:</Text>
                      <Text>{viewData.employeeFullName || '-'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Employee ID:</Text>
                      <Text>{viewData.employeeId || '-'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Job Title:</Text>
                      <Text>{viewData.jobTitle || '-'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Department:</Text>
                      <Text>{viewData.department || '-'}</Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* Email Details */}
                <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                  <Text fontWeight="bold" fontSize="lg" mb={3}>Email Details</Text>
                  <VStack align="stretch" spacing={2}>
                    <HStack align="start">
                      <Text fontWeight="semibold" minW="150px">Subject:</Text>
                      <Text>{viewData.emailSubject || '-'}</Text>
                    </HStack>
                    <Box>
                      <Text fontWeight="semibold" mb={2}>Content:</Text>
                      <Box
                        p={3}
                        bg="white"
                        borderWidth="1px"
                        borderRadius="md"
                        maxH="200px"
                        overflowY="auto"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewData.emailContent || '<p>No content</p>') }}
                      />
                    </Box>
                  </VStack>
                </Box>

                {/* Verification Timeline */}
                <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                  <Text fontWeight="bold" fontSize="lg" mb={3}>Timeline</Text>
                  <VStack align="stretch" spacing={2}>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Created:</Text>
                      <Text>{viewData.createdAt ? DateFormatter(viewData.createdAt) : '-'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Sent:</Text>
                      <Text>{viewData.sentAt ? DateFormatter(viewData.sentAt) : '-'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="semibold" minW="150px">Expires:</Text>
                      <Text>{viewData.expiryDate ? DateFormatter(viewData.expiryDate) : '-'}</Text>
                    </HStack>
                    {viewData.response?.submittedAt && (
                      <HStack>
                        <Text fontWeight="semibold" minW="150px">Submitted:</Text>
                        <Text>{DateFormatter(viewData.response.submittedAt)}</Text>
                      </HStack>
                    )}
                  </VStack>
                </Box>

                {/* Response Data (if completed) */}
                {viewData.response && viewData.status === 'completed' && (
                  <Box borderWidth="1px" borderRadius="md" p={4} bg="green.50">
                    <Text fontWeight="bold" fontSize="lg" mb={3} color="green.700">
                      Verification Response
                    </Text>
                    <VStack align="stretch" spacing={4}>
                      {/* Employment Verification */}
                      <Box>
                        <Text fontWeight="bold" mb={2}>Employment Verification</Text>
                        <VStack align="stretch" spacing={1} pl={4}>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" minW="200px">Was Employed:</Text>
                            <Text fontSize="sm">{viewData.response.wasEmployed?.toUpperCase() || '-'}</Text>
                          </HStack>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" minW="200px">Job Title:</Text>
                            <Text fontSize="sm">{viewData.response.officialJobTitle || '-'}</Text>
                          </HStack>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" minW="200px">Employment Type:</Text>
                            <Text fontSize="sm">{viewData.response.employmentType?.replace('_', ' ').toUpperCase() || '-'}</Text>
                          </HStack>
                        </VStack>
                      </Box>

                      {/* Performance & Conduct */}
                      <Box>
                        <Text fontWeight="bold" mb={2}>Performance & Conduct</Text>
                        <VStack align="stretch" spacing={1} pl={4}>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" minW="200px">Performance Rating:</Text>
                            <Text fontSize="sm">{viewData.response.performanceRating?.replace('_', ' ').toUpperCase() || '-'}</Text>
                          </HStack>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" minW="200px">Met Expectations:</Text>
                            <Text fontSize="sm">{viewData.response.metExpectations?.toUpperCase() || '-'}</Text>
                          </HStack>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" minW="200px">Adhered to Policies:</Text>
                            <Text fontSize="sm">{viewData.response.adheredToPolicies?.toUpperCase() || '-'}</Text>
                          </HStack>
                        </VStack>
                      </Box>

                      {/* Separation Details */}
                      <Box>
                        <Text fontWeight="bold" mb={2}>Separation Details</Text>
                        <VStack align="stretch" spacing={1} pl={4}>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" minW="200px">Separation Reason:</Text>
                            <Text fontSize="sm">{viewData.response.separationReason?.replace('_', ' ').toUpperCase() || '-'}</Text>
                          </HStack>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" minW="200px">Eligible for Rehire:</Text>
                            <Text fontSize="sm">{viewData.response.eligibleForRehire?.replace('_', ' ').toUpperCase() || '-'}</Text>
                          </HStack>
                        </VStack>
                      </Box>

                      {/* Verifier Details */}
                      <Box>
                        <Text fontWeight="bold" mb={2}>Verifier Information</Text>
                        <VStack align="stretch" spacing={1} pl={4}>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" minW="200px">Verifier Name:</Text>
                            <Text fontSize="sm">{viewData.response.verifierName || '-'}</Text>
                          </HStack>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" minW="200px">Designation:</Text>
                            <Text fontSize="sm">{viewData.response.verifierDesignation || '-'}</Text>
                          </HStack>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" minW="200px">Email:</Text>
                            <Text fontSize="sm">{viewData.response.verifierEmail || '-'}</Text>
                          </HStack>
                        </VStack>
                      </Box>

                      {/* Additional Comments */}
                      {viewData.response.additionalComments && (
                        <Box>
                          <Text fontWeight="bold" mb={2}>Additional Comments</Text>
                          <Text fontSize="sm" pl={4}>{viewData.response.additionalComments}</Text>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                )}
              </VStack>
            ) : (
              <Text>No data available</Text>
            )}
          </ModalBody>

          <ModalFooter>
            <Button bg="#3C2FC0" color="white" _hover={{ bg: "#2F23A0" }} onClick={onViewClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Background Verification
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this background verification? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                ml={3}
                isLoading={deletingId !== null}
                loadingText="Deleting..."
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Bulk Delete Modal */}
      <CommonDeleteModel
        isOpen={deleteMany}
        onClose={() => {
          setDeleteMany(false);
          setSelectedValues([]);
        }}
        type={`Background Verification${selectedValues.length > 1 ? 's' : ''}`}
        handleDeleteData={handleBulkDelete}
        ids={selectedValues}
        onCancel={() => setSelectedValues([])}
      />
    </div>
  );
};

export default BackgroundVerification;

